import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { UnleashClient, IConfig } from 'unleash-proxy-client';
import { FeatureFlag } from '../enums/feature-flag.enum';
import { ENV_CONFIG, EnvConfig } from '../infrastructure/env-config.token';

@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private readonly env: EnvConfig = inject(ENV_CONFIG);
  private readonly client: UnleashClient | undefined = this.createClient();
  private readonly ready: WritableSignal<boolean> = signal<boolean>(false);
  private readonly version: WritableSignal<number> = signal<number>(0);

  isReady: Signal<boolean> = this.ready.asReadonly();

  start(): void {
    if (!this.client) return;
    this.client.on('ready', () => {
      this.ready.set(true);
      this.version.update((v: number) => v + 1);
    });
    this.client.on('update', () => this.version.update((v: number) => v + 1));
    this.client.on('error', () => this.ready.set(true));
    void this.client.start();
  }

  setUserId(userId: string | null): void {
    if (!this.client) return;
    void this.client.updateContext({ userId: userId ?? undefined });
  }

  isEnabled(name: FeatureFlag): Signal<boolean> {
    return computed(() => {
      this.version();
      return this.client?.isEnabled(name) ?? false;
    });
  }

  isEnabledNow(name: FeatureFlag): boolean {
    return this.client?.isEnabled(name) ?? false;
  }

  private createClient(): UnleashClient | undefined {
    const cfg: EnvConfig['unleash'] = this.env.unleash;
    if (!cfg) return undefined;

    const config: IConfig = {
      url: cfg.url,
      clientKey: cfg.clientKey,
      appName: cfg.appName,
      refreshInterval: 30,
      disableMetrics: false
    };
    return new UnleashClient(config);
  }
}
